class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      /* PERF 2026-10-13: removing Retailer+ or the last product is ONE update.js that also clears the
         member code when the discount manager would (see planRemove); everything else is today's change.js. */
      const plan = typeof cartItems.planRemove === 'function' ? cartItems.planRemove(this) : null;
      cartItems.updateQuantity(this.dataset.index, 0, null, null, plan);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      /* PERF 2026-10-13: the gh3 / Bundle this / quick-add paths publish AFTER they have rendered the
         drawer from their own write (drawerRendered). Re-fetching it here was a wasted 11-36 KB GET,
         parse and wallet re-mount on every add, and a late one could paint the pre-discount cart. */
      if (event && event.drawerRendered && this.tagName === 'CART-DRAWER-ITEMS') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      /* Render the drawer section from the market ROOT, never from /cart:
         cart-drawer.liquid gates the express wallets on request.page_type != 'cart'
         (the /cart page's own footer owns content_for_additional_checkout_buttons),
         so a section render fetched from /cart comes back with NO Shop Pay / Apple Pay
         / G Pay and this replace() silently strips them out of the open drawer.
         2026-08-24 */
      fetch(`${(routes.cart_url || '/cart').replace(/cart\/?$/, '')}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  /* 2026-10-13: re-render the cart page (items, footer, bubble, live region) from the server. The discount
     manager (theme.liquid) calls it after it applies / clears the code or heals a line on /cart; its old
     refresh only ever touched the drawer, so /cart kept showing the old prices until a reload. */
  refreshFromServer() {
    const sections = this.getSectionsToRender();
    return fetch(`${window.location.pathname}?sections=${sections.map((s) => s.section).join(',')}`)
      .then((r) => r.json())
      .then((parsed) => {
        const src = parsed[sections[0].section] && new DOMParser().parseFromString(parsed[sections[0].section], 'text/html').querySelector('cart-items');
        if (src) {
          const empty = src.classList.contains('is-empty');
          this.classList.toggle('is-empty', empty);
          const footer = document.getElementById('main-cart-footer');
          if (footer) footer.classList.toggle('is-empty', empty);
        }
        sections.forEach((section) => {
          if (!parsed[section.section]) return;
          const host = document.getElementById(section.id);
          if (!host) return;
          const el = host.querySelector(section.selector) || host;
          el.innerHTML = this.getSectionInnerHTML(parsed[section.section], section.selector);
        });
      })
      .catch(() => {});
  }

  /* PERF 2026-10-13 (C7): one write for the two removes that used to take two. The second write was
     the discount manager clearing RETAILER-Q4M8VT ~1 s later, and its drawer refresh never ran, so the
     drawer kept the member price after Retailer+ was gone ($5.00 shown, $7.69 charged: B1). Same rule
     as the manager (theme.liquid v23): the code stays only while the cart has a product AND
     (Retailer+, a signed-in member, or a gap funnel cart). Returns the update.js body, or null for
     today's change.js path. */
  planRemove(btn) {
    const scope = this;
    const line = btn.closest('[data-line-key]');
    const banner = scope.querySelector('.cart-item--membership-banner[data-line-key]');
    const rows = Array.from(scope.querySelectorAll('.cart-item[data-line-key]'));
    if (!line) return null;
    const key = line.dataset.lineKey;
    const memberKey = banner ? banner.dataset.lineKey : null;
    const clearCookie = () => {
      document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    };
    if (banner && line === banner) {
      const funnel = (banner.dataset.funnel || '') + '';
      const keepsCode = rows.length > 0 && (banner.dataset.member === '1' || funnel.indexOf('gap') === 0);
      const body = { updates: { [memberKey]: 0 } };
      if (!keepsCode) {
        body.discount = '';
        body.onDone = clearCookie;
      }
      return body;
    }
    if (rows.length === 1 && rows[0] === line) {
      /* the last product: the manager clears every code on a cart with no product (v17 lock). D2 (Owen)
         decides whether Retailer+ goes with it; until then it stays, exactly as today. */
      const updates = { [key]: 0 };
      if (memberKey && window.TRC_REMOVE_PLUS_WITH_LAST === true) updates[memberKey] = 0;
      return { updates, discount: '', onDone: clearCookie };
    }
    return null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId, plan) {
    this.enableLoading(line);

    const sections = this.getSectionsToRender().map((section) => section.section);
    let url = `${routes.cart_change_url}`;
    /* GHOST-1022 (Owen, IG browser SGD): address the line by its KEY, not its position. Positions go stale when
       another script changes the cart under the open drawer (twin swap, discount manager, Retailer+ line), and
       change.js then answers "line parameter is invalid" and the row stays as a $0 / qty 0 ghost. */
    const rowEl = this.lineRow(line);
    const lineKey = rowEl && rowEl.dataset.lineKey;
    let payload = lineKey
      ? { id: lineKey, quantity, sections, sections_url: window.location.pathname }
      : { line, quantity, sections, sections_url: window.location.pathname };
    let onDone = null;
    if (plan && plan.updates) {
      /* PERF 2026-10-13 (C7): the planned remove as one update.js, keyed by line key */
      url = `${routes.cart_update_url}`;
      onDone = plan.onDone || null;
      payload = { updates: plan.updates, sections, sections_url: window.location.pathname };
      if (Object.prototype.hasOwnProperty.call(plan, 'discount')) payload.discount = plan.discount;
    }
    const body = JSON.stringify(payload);

    fetch(url, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (onDone && parsedState && parsedState.items) onDone();
        this.applyState(parsedState, line, name, variantId);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        /* GHOST-1022: no red error; re-read the cart and redraw it as it really is */
        this.quietResync();
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /* 2026-10-13: the change.js response handling, unchanged, shared with the planned update.js removes */
  applyState(parsedState, line, name, variantId) {
    const quantityElement =
      document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
    const items = document.querySelectorAll('.cart-item');

    if (parsedState.errors || (parsedState.status && !parsedState.items)) {
      if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
      /* GHOST-1022: a stock limit is worth telling the shopper; anything technical ("line parameter is invalid",
         a stale or missing line) is not. Either way the cart is re-read and redrawn, so no ghost row survives. */
      const raw = parsedState.errors || parsedState.description || parsedState.message;
      const txt = typeof raw === 'string' ? raw : JSON.stringify(raw || '');
      const technical = /parameter|invalid|not found|cannot find|no line|line item/i.test(txt);
      if (!technical) this.updateLiveRegions(line, raw);
      this.quietResync();
      return;
    }

    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      elementToReplace.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );
    });
    // Resolve the affected line by variant id (stable) rather than the
    // positional `line` captured before this fetch. The cart re-renders and
    // can re-order/insert lines mid-update (the auto-added Retailer+
    // membership line is skipped from render but still counts in the index),
    // which previously pinned the "you can only add N" notice onto the wrong
    // product row.
    let targetLine = line;
    let updatedItem = parsedState.items[line - 1];
    if (variantId) {
      const idx = parsedState.items.findIndex((it) => String(it.variant_id || it.id) === String(variantId));
      if (idx !== -1) {
        updatedItem = parsedState.items[idx];
        const freshInput = document.querySelector(`[data-quantity-variant-id="${variantId}"]`);
        if (freshInput && freshInput.dataset.index) targetLine = freshInput.dataset.index;
      }
    }
    // `quantityElement` is the pre-re-render input — it still holds the
    // quantity the customer asked for, so comparing it against the server's
    // resulting quantity is what detects a clamp ("you can only add N").
    const updatedValue = updatedItem ? updatedItem.quantity : undefined;
    let message = '';
    if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
      if (typeof updatedValue === 'undefined') {
        message = window.cartStrings.error;
      } else {
        message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
      }
    }
    this.updateLiveRegions(targetLine, message);

    const lineItem =
      document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
      cartDrawerWrapper
        ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
        : lineItem.querySelector(`[name="${name}"]`).focus();
    } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
      trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
    } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
      trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
    }

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
  }

  /* GHOST-1022: the row element for a 1-based line number from the current render */
  lineRow(line) {
    const byId = document.getElementById(`CartDrawer-Item-${line}`) || document.getElementById(`CartItem-${line}`);
    if (byId && this.contains(byId)) return byId;
    const el = this.querySelector(`[data-index="${line}"]`);
    return el ? el.closest('[data-line-key]') : null;
  }

  /* GHOST-1022: redraw from the server's cart after a failed write (drawer: market-root section render; page: refreshFromServer) */
  quietResync() {
    try {
      if (this.tagName === 'CART-DRAWER-ITEMS') this.onCartUpdate();
      else if (typeof this.refreshFromServer === 'function') this.refreshFromServer();
    } catch (e) {}
  }

  updateLiveRegions(line, message) {
    // Shopify can return cart errors as an object or array (e.g. a cart-validation
    // function), which would render literally as "[object Object]". Coerce to a
    // readable string so customers never see a broken/scam-looking error.
    if (message && typeof message !== 'string') {
      const fallback = (window.cartStrings && window.cartStrings.error) || 'There was an error updating your cart.';
      if (Array.isArray(message)) {
        message = message.map((e) => (e && (e.message || e.description)) || '').filter(Boolean).join(' ') || fallback;
      } else {
        message = message.message || message.description || fallback;
      }
    }
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    // Clear any stale line-item error (e.g. a leftover "you can only add N")
    // before this update so it can't linger on an unrelated row.
    mainCartItems.querySelectorAll('.cart-item__error-text').forEach((el) => (el.textContent = ''));

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (!mainCartItems) return;
    mainCartItems.classList.remove('cart__items--disabled');

    // Hide every spinner in the live cart, not just this line's. The cart
    // re-renders/re-orders during an update, so a line-scoped query can miss the
    // spinner that was actually shown and leave it stuck on top of a price
    // (the CSS hides the price-wrapper while a spinner is visible). Updates are
    // debounced and disabled while loading, so no second spinner legitimately
    // needs to stay visible.
    mainCartItems.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
